import {
  CollectionType,
  DEFAULT_CLINICAL_DATA,
  DEFAULT_CONSENT_DATA,
  DEFAULT_CONSENT_FORM_DATA,
  DEFAULT_CONSENTED_TEXT,
  DEFAULT_FAMILY_DATA,
  DEFAULT_FORM_DATA,
  DEFAULT_ORDER_DATA,
  DEFAULT_PATIENT_DATA,
  DEFAULT_REASON_DATA,
  ParentFieldShape,
  PartialRequisitionFormData,
  PhenotypicTrait,
  RequisitionFormData,
} from '@/types/RequisitionFormTypes'

/**
 * Default empty values for the legacy two-fixed-slot biological-parent shape.
 *
 * P9 (MOTHER/FATHER migration): these constants used to live next to
 * `DEFAULT_FORM_DATA` in `RequisitionFormTypes.ts` and were spread into the
 * default form. They have been confined here because this module — the
 * legacy submit cleanup + IMDB-load merge adapter — is the only remaining
 * consumer. Working ordering state never seeds empty parent slots; comparators
 * are the source of truth.
 */
export const DEFAULT_MOTHER_DATA: ParentFieldShape = {
  parentType: 'mother',
  parentFirstName: '',
  parentLastName: '',
  parentDateOfBirth: '',
  parentMRN: '',
  parentPhone: '',
  parentEmail: '',
  phenotypicTrait: PhenotypicTrait.Unknown,
  hpoTerms: [],
  icd10Terms: '',
  collectionType: CollectionType.Unknown,
}

export const DEFAULT_FATHER_DATA: ParentFieldShape = {
  parentType: 'father',
  parentFirstName: '',
  parentLastName: '',
  parentDateOfBirth: '',
  parentMRN: '',
  parentPhone: '',
  parentEmail: '',
  phenotypicTrait: PhenotypicTrait.Unknown,
  hpoTerms: [],
  icd10Terms: '',
  collectionType: CollectionType.Unknown,
}

export function cleanupData(formData: RequisitionFormData): RequisitionFormData {
  const cleanedFormData = cleanupParentData(formData)
  return cleanedFormData
}

/**
 * Removes fields from partial data that are equivalent to their default values.
 * This helps reduce payload size by only including fields that differ from defaults.
 */
export function cleanupPartialData(formData: RequisitionFormData): PartialRequisitionFormData {
  const cleanedFormData = cleanupParentData(formData)
  const partialData: PartialRequisitionFormData = {}

  // Clean up order data
  if (cleanedFormData.order) {
    const cleanedOrder = removeDefaultFields(cleanedFormData.order, DEFAULT_ORDER_DATA)
    if (cleanedFormData.order.lab_name !== undefined) {
      cleanedOrder.lab_name = cleanedFormData.order.lab_name
    }
    if (Object.keys(cleanedOrder).length > 0) {
      partialData.order = cleanedOrder
    }
  }

  // Clean up patient data
  if (cleanedFormData.patient) {
    const cleanedPatient = removeDefaultFields(cleanedFormData.patient, DEFAULT_PATIENT_DATA)
    if (Object.keys(cleanedPatient).length > 0) {
      partialData.patient = cleanedPatient
    }
  }

  // Clean up clinical data
  if (cleanedFormData.clinical) {
    const cleanedClinical = removeDefaultFields(cleanedFormData.clinical, DEFAULT_CLINICAL_DATA)
    if (Object.keys(cleanedClinical).length > 0) {
      partialData.clinical = cleanedClinical
    }
  }

  // Clean up reason data
  if (cleanedFormData.reason) {
    const cleanedReason = removeDefaultFields(cleanedFormData.reason, DEFAULT_REASON_DATA)
    if (Object.keys(cleanedReason).length > 0) {
      partialData.reason = cleanedReason
    }
  }

  // Clean up family data
  if (cleanedFormData.family) {
    const cleanedFamily = removeDefaultFields(cleanedFormData.family, DEFAULT_FAMILY_DATA)
    if (Object.keys(cleanedFamily).length > 0) {
      partialData.family = cleanedFamily
    }
  }

  // Clean up mother data
  if (cleanedFormData.mother) {
    const cleanedMother = removeDefaultFields(cleanedFormData.mother, DEFAULT_MOTHER_DATA)
    if (Object.keys(cleanedMother).length > 0) {
      partialData.mother = cleanedMother
    }
  }

  // Clean up father data
  if (cleanedFormData.father) {
    const cleanedFather = removeDefaultFields(cleanedFormData.father, DEFAULT_FATHER_DATA)
    if (Object.keys(cleanedFather).length > 0) {
      partialData.father = cleanedFather
    }
  }

  // Always include consent data - required by IMDB backend
  if (cleanedFormData.consent) {
    // Don't filter consent - always include it even if it matches defaults
    // The backend needs providerEmail which may match default structure
    partialData.consent = cleanedFormData.consent
  }

  // Clean up consent form data
  if (cleanedFormData.consentFormData) {
    const cleanedConsentForm = removeDefaultFields(cleanedFormData.consentFormData, DEFAULT_CONSENT_FORM_DATA)
    if (Object.keys(cleanedConsentForm).length > 0) {
      partialData.consentFormData = cleanedConsentForm
    }
  }

  return partialData
}

/**
 * Recursively removes fields from an object that match their default values.
 * Handles primitives, arrays, and nested objects.
 */
function removeDefaultFields<T extends object>(data: T, defaults: T): Partial<T> {
  const cleaned: Partial<T> = {}

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = (data as Record<string, unknown>)[key]
      const defaultValue = (defaults as Record<string, unknown>)[key]

      // Skip if value is undefined
      if (value === undefined) {
        continue
      }

      // Handle arrays
      if (Array.isArray(value)) {
        const defaultArray = Array.isArray(defaultValue) ? defaultValue : []
        if (!areArraysEqual(value, defaultArray)) {
          ;(cleaned as Record<string, unknown>)[key] = value
        }
      }
      // Handle objects (but not arrays, which are also objects in JS)
      else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (typeof defaultValue === 'object' && defaultValue !== null && !Array.isArray(defaultValue)) {
          const cleanedNested = removeDefaultFields(value as object, defaultValue as object)
          if (Object.keys(cleanedNested).length > 0) {
            ;(cleaned as Record<string, unknown>)[key] = cleanedNested
          }
        } else {
          // If default is not an object, keep the value
          ;(cleaned as Record<string, unknown>)[key] = value
        }
      }
      // Handle primitives
      else {
        if (value !== defaultValue) {
          ;(cleaned as Record<string, unknown>)[key] = value
        }
      }
    }
  }

  return cleaned
}

/**
 * Compares two arrays for equality (shallow comparison).
 */
function areArraysEqual<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) {
    return false
  }
  return arr1.every((item, index) => {
    const item2 = arr2[index]
    // Deep comparison for objects in arrays
    if (typeof item === 'object' && item !== null && typeof item2 === 'object' && item2 !== null) {
      return JSON.stringify(item) === JSON.stringify(item2)
    }
    return item === item2
  })
}

/** A parent slot carries meaningful data when the user filled a name or MRN. */
function parentHasMeaningfulData(parent: RequisitionFormData['mother']): boolean {
  return Boolean(
    parent && (parent.parentFirstName?.trim() || parent.parentLastName?.trim() || parent.parentMRN?.trim())
  )
}

/**
 * Cleans up parent data before submission by keeping only parent slots the user
 * actually filled.
 *
 * Decoupling (R5): which parents to send is determined by the data the user
 * entered (the comparator-derived family structure), not the chosen test type's
 * `parentSamplesRequired` tier — a multi-tier category can submit a duo/trio
 * order on a SKU whose config tier is 0, so a tier-based gate would wrongly drop
 * real parent data. Any slot with no meaningful data is removed so we never send
 * empty stubs (which break save); a proband-only order naturally clears both
 * because its slots are empty.
 *
 * @param formData - The form data to clean up
 * @returns A new form data object with empty parent slots removed
 */
export function cleanupParentData(formData: RequisitionFormData): RequisitionFormData {
  const cleanedFormData = { ...formData }

  if (!parentHasMeaningfulData(cleanedFormData.mother)) {
    cleanedFormData.mother = undefined
  }
  if (!parentHasMeaningfulData(cleanedFormData.father)) {
    cleanedFormData.father = undefined
  }
  return cleanedFormData
}

/**
 * Deep merges a value with its default, replacing null/undefined with default values.
 * Handles primitives, arrays, and nested objects.
 */
function mergeValueWithDefault<T>(value: T | null | undefined, defaultValue: T): T {
  // If value is null or undefined, use default
  if (value === null || value === undefined) {
    return defaultValue
  }

  // If default is an array, handle array merging
  if (Array.isArray(defaultValue)) {
    // If value is also an array, use it (even if empty, as empty array is valid)
    if (Array.isArray(value)) {
      return value as T
    }
    // If value is not an array, use default
    return defaultValue
  }

  // If default is an object (and not an array), handle object merging
  if (typeof defaultValue === 'object' && defaultValue !== null) {
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !(value instanceof Date) &&
      !(value instanceof RegExp)
    ) {
      // Recursively merge object properties
      const merged = { ...defaultValue }
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          const defaultVal = (defaultValue as Record<string, unknown>)[key]
          const partialVal = (value as Record<string, unknown>)[key]
          if (defaultVal !== undefined) {
            ;(merged as Record<string, unknown>)[key] = mergeValueWithDefault(partialVal, defaultVal)
          } else {
            // If key doesn't exist in default, use the value (if not null/undefined)
            if (partialVal !== null && partialVal !== undefined) {
              ;(merged as Record<string, unknown>)[key] = partialVal
            }
          }
        }
      }
      return merged as T
    }
    // If value is not an object, use default
    return defaultValue
  }

  // For primitives, use the value (already checked it's not null/undefined)
  return value
}

/**
 * Merges partial form data with default data, replacing null/undefined values with defaults.
 * This is used when loading existing orders from the API that may have null fields.
 *
 * @param partialData - Partial form data from the API (may contain null/undefined values)
 * @returns Complete form data with all null/undefined values replaced with defaults
 */
export function mergeWithDefaults(partialData: PartialRequisitionFormData): RequisitionFormData {
  const merged: RequisitionFormData = {
    ...DEFAULT_FORM_DATA,
  }

  // Merge order data
  if (partialData.order) {
    merged.order = mergeValueWithDefault(partialData.order, DEFAULT_ORDER_DATA) as typeof DEFAULT_ORDER_DATA
  }

  // Merge patient data
  if (partialData.patient) {
    merged.patient = mergeValueWithDefault(partialData.patient, DEFAULT_PATIENT_DATA) as typeof DEFAULT_PATIENT_DATA
  }

  // Merge clinical data
  if (partialData.clinical) {
    merged.clinical = mergeValueWithDefault(partialData.clinical, DEFAULT_CLINICAL_DATA) as typeof DEFAULT_CLINICAL_DATA
  }

  // Merge reason data
  if (partialData.reason) {
    merged.reason = mergeValueWithDefault(partialData.reason, DEFAULT_REASON_DATA) as typeof DEFAULT_REASON_DATA
  }

  // Merge family data
  if (partialData.family) {
    merged.family = mergeValueWithDefault(partialData.family, DEFAULT_FAMILY_DATA) as typeof DEFAULT_FAMILY_DATA
  }

  // Merge mother data
  if (partialData.mother) {
    merged.mother = mergeValueWithDefault(partialData.mother, DEFAULT_MOTHER_DATA) as typeof DEFAULT_MOTHER_DATA
  }

  // Merge father data
  if (partialData.father) {
    merged.father = mergeValueWithDefault(partialData.father, DEFAULT_FATHER_DATA) as typeof DEFAULT_FATHER_DATA
  }

  // Merge consent data
  if (partialData.consent) {
    merged.consent = mergeValueWithDefault(partialData.consent, DEFAULT_CONSENT_DATA) as typeof DEFAULT_CONSENT_DATA
  }

  // Merge consent form data
  if (partialData.consentFormData) {
    const defaultConsentFormData = {
      medicalProfessionalName: '',
      npi: '',
      signatureDate: '',
      signature: '',
      consentedText: DEFAULT_CONSENTED_TEXT,
    }
    merged.consentFormData = mergeValueWithDefault(
      partialData.consentFormData,
      defaultConsentFormData
    ) as typeof defaultConsentFormData
  }

  return merged
}
